import { TextField, useTheme, InputAdornment, CardMedia, Stack } from "@mui/material";
import { useState, ChangeEvent } from "react";
import { IconError } from "~components/Icons";
import ErrorSVG from "~assets/erroImage.svg";
import noImage from "~assets/noImage.svg";
import type { IPollCreation } from "~contexts/Socket/interfaces";
import { getStyles } from "./styles";

interface IProps {
  index: number;
  pollOnCreation: IPollCreation;
  setPollOnCreation: (value: IPollCreation) => void;
  isTextInvalid: boolean;
  isImageInvalid: boolean;
}

const IMAGE_OPTION_MAX_LENGTH = 12;
const TEXT_OPTION_MAX_LENGTH = 26;

const InputPoll = ({
  index,
  pollOnCreation,
  setPollOnCreation,
  isTextInvalid,
  isImageInvalid,
}: IProps) => {
  const [imageLoaded, setImageLoaded] = useState<boolean>(true);
  const [imageHasNoError, setImageHasNoError] = useState<boolean>(false);
  const imageURL = pollOnCreation[`image${index}`];
  const theme = useTheme();
  const { styles } = getStyles(theme);

  const checkIfImageExists = (url: string, callback: (value: boolean) => void) => {
    const img = new Image();
    img.src = url;
    if (img.complete) {
      callback(true);
    } else {
      img.onload = () => {
        callback(true);
        setImageLoaded(true);
        setImageHasNoError(false);
      };

      img.onerror = () => {
        callback(false);
        setImageHasNoError(true);
        setImageLoaded(false);
      };
    }
  };

  const imageValidation = (pollImage: string) => {
    checkIfImageExists(pollImage, (exists: boolean) => {
      if (exists || pollImage === "") {
        return pollImage;
      } else {
        return null;
      }
    });
  };

  const onImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPollOnCreation({ ...pollOnCreation, [`image${index}`]: e.target.value });
    imageValidation(e.target.value);
  };

  return (
    <>
      <Stack sx={styles.wrapper}>
        {imageURL === "" ? (
          <CardMedia sx={styles.CardMedia} component="img" image={noImage} />
        ) : (
          <CardMedia
            sx={styles.CardMedia}
            component="img"
            image={imageHasNoError && !imageLoaded ? ErrorSVG : imageURL}
          />
        )}
        <Stack sx={{ width: "100%" }}>
          <TextField
            size="small"
            variant="outlined"
            fullWidth
            value={imageURL}
            id={`image${index}`}
            placeholder="link da imagem"
            error={(imageHasNoError && imageURL !== "") || isImageInvalid}
            hiddenLabel={imageHasNoError && imageURL ? false : true}
            label={imageHasNoError && imageURL ? "link da imagem" : ""}
            onChange={(evt: ChangeEvent<HTMLInputElement>) => onImageChange(evt)}
            sx={imageHasNoError && imageURL ? styles.error : null}
            helperText={
              imageHasNoError && imageURL
                ? "Esse link não possui uma imagem. Altere o link para continuar a enquete"
                : ""
            }
            InputProps={{
              endAdornment: imageHasNoError && imageURL && (
                <InputAdornment position="end">
                  <IconError sx={{ marginRight: 0.5, color: "transparent" }} />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            size="small"
            variant="outlined"
            fullWidth
            hiddenLabel
            error={isTextInvalid}
            id={`option${index}`}
            placeholder="texto do botão"
            value={pollOnCreation[`option${index}`]}
            onChange={(e) =>
              setPollOnCreation({ ...pollOnCreation, [`option${index}`]: e.target.value })
            }
            inputProps={{
              maxLength: imageURL ? IMAGE_OPTION_MAX_LENGTH : TEXT_OPTION_MAX_LENGTH,
            }}
          />
        </Stack>
      </Stack>
    </>
  );
};

export default InputPoll;
